import { ApiClientError } from './client';

const WEAK_PASSWORD: Record<string, string> = {
  too_short: 'Das Passwort muss mindestens 10 Zeichen lang sein.',
  too_long: 'Das Passwort darf höchstens 128 Zeichen lang sein.',
  too_common: 'Dieses Passwort ist zu bekannt. Bitte wählen Sie ein anderes.',
  contains_personal_data: 'Das Passwort darf Ihren Namen oder Ihre E-Mail-Adresse nicht enthalten.',
};

/** Verständliche deutsche Meldung zu einem Fehler der API (nie technische Details anzeigen). */
export function messageFor(error: unknown): string {
  if (!(error instanceof ApiClientError)) {
    return 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
  }
  switch (error.code) {
    case 'WEAK_PASSWORD':
      return Array.isArray(error.details)
        ? error.details
            .map((code) => (typeof code === 'string' ? WEAK_PASSWORD[code] : undefined))
            .filter((text): text is string => text !== undefined)
            .join(' ') || 'Das Passwort ist nicht zulässig.'
        : 'Das Passwort ist nicht zulässig.';
    case 'VALIDATION_ERROR':
      return 'Bitte prüfen Sie Ihre Eingaben.';
    case 'RATE_LIMITED':
      return 'Zu viele Versuche. Bitte warten Sie einen Moment und versuchen Sie es dann erneut.';
    case 'NETWORK':
    case 'INVALID_CREDENTIALS':
    case 'EMAIL_NOT_VERIFIED':
    case 'INVALID_TOKEN':
    case 'WRONG_PASSWORD':
    case 'INVITATION_EMAIL_MISMATCH':
    case 'TEAM_CHANGED':
    case 'CODE_TAKEN':
    case 'INVALID_EXTERNAL_CODE':
    case 'INVALID_IMAGE':
    case 'IMAGE_INVALID':
    case 'IMAGE_PROCESSING_UNAVAILABLE':
    case 'ITEM_IMAGES_DISABLED':
    case 'IMAGE_TOO_LARGE':
    case 'RECOGNITION_TIMEOUT':
    case 'RECOGNITION_FAILED':
    case 'RECOGNITION_INVALID_RESPONSE':
    case 'RECOGNITION_DAILY_LIMIT':
    case 'RECOGNITION_PLATFORM_LIMIT':
    case 'RECOGNITION_EVENT_LIMIT':
    case 'NO_OPEN_ITEMS':
    case 'NOT_IN_LIST':
    case 'LAST_OWNER':
    case 'ALREADY_MEMBER':
    case 'LIMIT_REACHED':
    case 'EVENT_LOCKED':
    case 'ACCOUNT_NOT_FOUND':
    case 'INVALID_TRANSITION':
    case 'INVALID_TIMELINE':
    case 'HAS_REGISTRATIONS':
    case 'LICENSE_NOT_ACTIVE':
    case 'LICENSE_DOWNGRADE':
    case 'LICENSE_TIER_TOO_SMALL':
    case 'LICENSE_LIMIT_REACHED':
    case 'LICENSE_NOT_ACTIVATABLE':
    case 'INVALID_PROMO_CODE':
    case 'PROMO_CODE_EXPIRED':
    case 'PROMO_CODE_NOT_USABLE_YET':
    case 'PROMO_CODE_LIMIT_REACHED':
    case 'PROMO_CODE_WRONG_TIER':
    case 'PROMO_CODE_ALREADY_USED':
    case 'PROMO_CODE_EXISTS':
    case 'REGISTRATION_CLOSED':
    case 'REGISTRATION_CANCELLED':
    case 'ALREADY_REGISTERED':
    case 'SELLER_LIMIT_REACHED':
    case 'SELLER_HAS_ITEMS':
    case 'NOT_APPROVED':
    case 'ITEM_LIMIT_REACHED':
    case 'ITEM_LOCKED':
    case 'ITEM_DELETED':
    case 'ITEM_STATE':
    case 'PRICE_LOCKED':
    case 'INVALID_PRICE':
    case 'NO_LABELS':
    case 'CODE_IN_USE':
    case 'CODE_REVOKED':
    case 'POOL_LIMIT_REACHED':
    case 'INVALID_COUNT':
    case 'INTAKE_CLOSED':
    case 'INVALID_CODE':
    case 'UNKNOWN_CODE':
    case 'RETURNS_NOT_OPEN':
    case 'COUNT_MISMATCH':
    case 'PICKUP_NOT_OPEN':
    case 'ALREADY_UNDONE':
    case 'NOT_LATEST_REST':
    case 'UNDO_EXPIRED':
    case 'BASKET_MODE_OFF':
    case 'NO_RETURNS':
    case 'BUSY':
    case 'IMPORT_INVALID':
    case 'PHONE_REQUIRED':
    case 'OPEN_ACCOUNT_ITEMS':
    case 'OPEN_REGISTER':
    case 'SHIFT_IN_USE':
    case 'SHIFT_FULL':
    case 'SIGNUP_NOT_OPEN':
    case 'SHIFT_STARTED':
    case 'SHIFT_TIME_MISSING':
    case 'SLOT_FULL':
    case 'ALREADY_BOOKED':
    case 'SLOTS_NOT_OPEN':
    case 'SLOT_STARTED':
    case 'SLOT_LIMIT':
    case 'SLOT_HAS_BOOKINGS':
    case 'CAPACITY_BELOW_BOOKINGS':
    case 'REGISTRATION_NOT_APPROVED':
    case 'KIND_MISMATCH':
    case 'CAPACITY_BELOW_OCCUPANCY':
    case 'TOO_MANY_SIGNUPS':
    case 'INVITE_INACTIVE':
    case 'LINK_UNREADABLE':
    case 'POSTER_NEEDS_APPROVAL':
    case 'ALREADY_TEAM':
    case 'ROLE_CHANGED':
    case 'NO_LINK':
    case 'EVENT_END_MISSING':
    case 'EVENT_NOT_PUBLIC':
    case 'NOT_ACTIVE':
    case 'PLATFORM_ADMIN':
    case 'INVALID_TOTP':
    case 'TOTP_ALREADY_ENABLED':
    case 'TOTP_NOT_ENABLED':
    case 'TOTP_SETUP_MISSING':
    case 'SETTLEMENT_NOT_READY':
    case 'SETTLEMENT_MISSING':
    case 'ALREADY_SETTLED':
    case 'NOT_SETTLED':
    case 'OPEN_ISSUES':
    case 'PAYOUT_DECREASED':
    case 'NO_BANK_ACCOUNT':
    case 'BANK_ACCOUNT_UNREADABLE':
    case 'NO_ORG_ACCOUNT':
    case 'NOTHING_TO_PAY':
    case 'NOT_TRANSFER':
    case 'PAYOUT_MISMATCH':
    case 'PAYOUT_NOT_CONFIGURED':
    case 'REPORT_TOO_LARGE':
    case 'WAIVER_REQUIRED':
    case 'LEGAL_TEXT_OUTDATED':
    case 'TERMS_OUTDATED':
    case 'SMTP_CREDENTIALS_MIXED':
    case 'MESSAGING_CLOSED':
    case 'REPLY_NOT_ALLOWED':
    case 'NO_RECIPIENTS':
    case 'TOO_MANY_RECIPIENTS':
    case 'BROADCAST_LIMIT':
    case 'NEWS_NOTICE_LIMIT':
    case 'THREAD_LIMIT':
    case 'INVALID_RECIPIENT':
      // Diese Texte kommen bereits verständlich vom Server bzw. Client (Positivliste, dazu die fachlichen Ablehnungen
      // zu Basar, Anmeldung, Artikeln, Etiketten und Annahme)
      return error.message;
    case 'FORBIDDEN':
      return 'Dafür fehlt Ihnen die Berechtigung.';
    case 'NOT_FOUND':
      return 'Das wurde nicht gefunden.';
    default:
      return 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
  }
}
